// Standalone page reachable via /set-password?token=... — used by both
// the invite flow (admin created the account, user picks first password)
// and the forgot-password flow (user requested a reset).
//
// On mount we ping /password-reset/check?token=... to validate the token
// and fetch the recipient's email + flow kind so we can render the right
// headline. On success we POST /password-reset/consume and redirect to
// the login page; the orchestrator revokes any existing sessions for
// the user as part of the consume step (belt-and-braces).
import { useCallback, useEffect, useState } from "react";
import { ApiError, httpClient, type ResetTokenCheck } from "../../client/http-client";

const cardClassName =
	"tg-surface tg-text-primary w-full max-w-[560px] rounded-[18px] p-6 shadow-[var(--shadow-tg-elevated)]";
const inputClassName =
	"tg-input tg-text-primary h-12 w-full rounded-[16px] px-4";
const primaryButtonClassName =
	"tg-btn-primary inline-flex h-10 items-center justify-center rounded-xl px-4 text-[0.95rem] font-semibold disabled:cursor-wait disabled:opacity-60";
const linkButtonClassName =
	"tg-text-secondary hover:tg-text-primary cursor-pointer text-[0.85rem] font-semibold underline-offset-4 hover:underline";

interface SetPasswordPageProps {
	token: string;
}

type Phase =
	| { kind: "loading" }
	| { kind: "invalid"; message: string }
	| { kind: "ready"; check: Required<ResetTokenCheck> }
	| { kind: "submitting"; check: Required<ResetTokenCheck> }
	| { kind: "done"; check: Required<ResetTokenCheck> };

export const SetPasswordPage = ({ token }: SetPasswordPageProps) => {
	const [phase, setPhase] = useState<Phase>({ kind: "loading" });
	const [password, setPassword] = useState("");
	const [confirm, setConfirm] = useState("");
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		let cancelled = false;
		if (!token) {
			setPhase({
				kind: "invalid",
				message:
					"This link is missing its token. Ask your administrator to send a fresh invitation.",
			});
			return;
		}
		(async () => {
			try {
				const result = await httpClient.auth.passwordReset.check(token);
				if (cancelled) return;
				if (!result.valid || !result.email || !result.kind) {
					setPhase({
						kind: "invalid",
						message:
							"This link is invalid or has expired. Ask your administrator to send a fresh invitation, or request a new password reset.",
					});
					return;
				}
				setPhase({
					kind: "ready",
					check: {
						valid: true,
						email: result.email,
						kind: result.kind,
					},
				});
			} catch (err) {
				if (cancelled) return;
				setPhase({
					kind: "invalid",
					message:
						err instanceof Error
							? err.message
							: "Couldn’t validate the link.",
				});
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [token]);

	const handleSubmit = useCallback(async () => {
		if (phase.kind !== "ready") return;
		setError(null);
		if (password.length < 8) {
			setError("Password must be at least 8 characters.");
			return;
		}
		if (password !== confirm) {
			setError("Passwords don’t match.");
			return;
		}
		setPhase({ kind: "submitting", check: phase.check });
		try {
			await httpClient.auth.passwordReset.consume(token, password);
			setPhase({ kind: "done", check: phase.check });
			// Brief pause so the user sees the success state, then redirect
			// to /login (root) where they can sign in with the new password.
			window.setTimeout(() => {
				window.location.replace("/");
			}, 1200);
		} catch (err) {
			setPhase({ kind: "ready", check: phase.check });
			if (err instanceof ApiError) {
				if (err.code === "TOKEN_INVALID") {
					setPhase({
						kind: "invalid",
						message:
							"This link is invalid or has expired. Ask your administrator to send a fresh invitation, or request a new password reset.",
					});
					return;
				}
				setError(err.message);
			} else {
				setError(err instanceof Error ? err.message : String(err));
			}
		}
	}, [confirm, password, phase, token]);

	const goToLogin = () => {
		window.location.replace("/");
	};

	if (phase.kind === "loading") {
		return (
			<section className={cardClassName}>
				<p className="tg-text-muted text-center text-[0.95rem]">
					Verifying your link…
				</p>
			</section>
		);
	}

	if (phase.kind === "invalid") {
		return (
			<section className={cardClassName}>
				<div>
					<span className="tg-text-secondary mb-2 inline-flex text-[0.72rem] font-semibold uppercase tracking-[0.13em]">
						Link unavailable
					</span>
					<h2 className="tg-text-primary max-w-[14ch] text-[clamp(1.95rem,2.8vw,2.8rem)] leading-[1.06] font-bold">
						Couldn’t use that link
					</h2>
					<p className="tg-text-muted mt-2.5 max-w-[46ch] text-[0.95rem] leading-7">
						{phase.message}
					</p>
				</div>
				<div className="mt-5">
					<button
						type="button"
						onClick={goToLogin}
						className={primaryButtonClassName}
					>
						Back to sign in
					</button>
				</div>
			</section>
		);
	}

	if (phase.kind === "done") {
		return (
			<section className={cardClassName}>
				<div>
					<span className="tg-text-secondary mb-2 inline-flex text-[0.72rem] font-semibold uppercase tracking-[0.13em]">
						All set
					</span>
					<h2 className="tg-text-primary max-w-[14ch] text-[clamp(1.95rem,2.8vw,2.8rem)] leading-[1.06] font-bold">
						Password saved
					</h2>
					<p className="tg-text-muted mt-2.5 max-w-[46ch] text-[0.95rem] leading-7">
						Redirecting you to the sign-in page…
					</p>
				</div>
			</section>
		);
	}

	const { check } = phase;
	const isInvite = check.kind === "invite";
	const eyebrow = isInvite ? "Welcome to Signal Lens" : "Reset your password";
	const headline = isInvite ? "Set your password" : "Choose a new password";
	const description = isInvite
		? `Choose a password for ${check.email}. You'll use it to sign in next time.`
		: `Pick a new password for ${check.email}. Existing sessions will be signed out.`;
	const cta = isInvite ? "Set password" : "Save new password";
	const submitting = phase.kind === "submitting";

	return (
		<section className={cardClassName}>
			<div>
				<span className="tg-text-secondary mb-2 inline-flex text-[0.72rem] font-semibold uppercase tracking-[0.13em]">
					{eyebrow}
				</span>
				<h2 className="tg-text-primary max-w-[14ch] text-[clamp(1.95rem,2.8vw,2.8rem)] leading-[1.06] font-bold">
					{headline}
				</h2>
				<p className="tg-text-muted mt-2.5 max-w-[46ch] text-[0.95rem] leading-7">
					{description}
				</p>
			</div>

			<form
				className="mt-5 grid gap-3.5"
				onSubmit={(event) => {
					event.preventDefault();
					void handleSubmit();
				}}
			>
				<label className="grid gap-2">
					<span className="tg-text-primary text-[0.92rem] font-semibold">
						New password
					</span>
					<input
						className={inputClassName}
						autoFocus
						value={password}
						onChange={(event) => setPassword(event.target.value)}
						placeholder="At least 8 characters"
						type="password"
						autoComplete="new-password"
						disabled={submitting}
					/>
				</label>

				<label className="grid gap-2">
					<span className="tg-text-primary text-[0.92rem] font-semibold">
						Confirm password
					</span>
					<input
						className={inputClassName}
						value={confirm}
						onChange={(event) => setConfirm(event.target.value)}
						placeholder="Re-enter the password"
						type="password"
						autoComplete="new-password"
						disabled={submitting}
					/>
				</label>

				<button
					className={primaryButtonClassName}
					type="submit"
					disabled={submitting}
				>
					{submitting ? "Saving…" : cta}
				</button>
			</form>

			{error ? (
				<div className="tg-alert-error mt-4 rounded-2xl px-4 py-3" role="alert">
					{error}
				</div>
			) : null}

			<p className="tg-text-muted mt-5 text-[0.82rem] leading-6">
				<button
					type="button"
					className={linkButtonClassName}
					onClick={goToLogin}
				>
					Back to sign in
				</button>
			</p>
		</section>
	);
};
